import { COLORS } from './visualizer';

export interface LyricLine {
  time: number;
  text: string;
}

export interface KaraokeState {
  lines: LyricLine[];
  positionSeconds: number;   // current playback position
}

/**
 * Binary search for the active line index — the last line whose
 * timestamp is <= position. Returns -1 if position precedes the
 * first line or lines is empty.
 */
export function findActiveLineIndex(lines: LyricLine[], position: number): number {
  if (lines.length === 0) return -1;
  if (position < lines[0].time) return -1;
  let lo = 0;
  let hi = lines.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1;
    if (lines[mid].time <= position) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return lo;
}

const ACTIVE_FONT_PX = 58;
const SURROUND_FONT_PX = 32;
const LINE_GAP_PX = 28;
const VISIBLE_RADIUS = 2;        // show ±2 lines around the active one

interface RenderedLine {
  text: string;
  offsetFromActive: number;
  opacity: number;
  fontPx: number;
}

/** Decide which lines to render and how, given the active index. */
export function selectVisibleLines(
  lines: LyricLine[],
  activeIdx: number,
): RenderedLine[] {
  const out: RenderedLine[] = [];
  if (activeIdx < 0) {
    // Before the first line — show the first 3 lines slightly dimmed,
    // with line 0 as the "coming up next" highlight
    for (let i = 0; i < Math.min(3, lines.length); i++) {
      out.push({
        text: lines[i].text,
        offsetFromActive: i + 1,    // all "below" the cursor
        opacity: i === 0 ? 0.6 : 0.4 - i * 0.1,
        fontPx: SURROUND_FONT_PX,
      });
    }
    return out;
  }
  for (let dist = -VISIBLE_RADIUS; dist <= VISIBLE_RADIUS; dist++) {
    const idx = activeIdx + dist;
    if (idx < 0 || idx >= lines.length) continue;
    const absDist = Math.abs(dist);
    const isActive = dist === 0;
    out.push({
      text: lines[idx].text,
      offsetFromActive: dist,
      opacity: isActive ? 1.0 : 0.55 - absDist * 0.18,
      fontPx: isActive ? ACTIVE_FONT_PX : SURROUND_FONT_PX,
    });
  }
  return out;
}

/**
 * Render the karaoke view: sky-tinted background + scrolling lyric lines
 * centered horizontally, active line large and bright.
 */
export function drawKaraoke(canvas: HTMLCanvasElement, state: KaraokeState): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const W = canvas.width;
  const H = canvas.height;

  // Background — match the visualizer's sky gradient for visual continuity
  const sky = ctx.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, COLORS.bg);
  sky.addColorStop(1, COLORS.sky);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, H);

  const activeIdx = findActiveLineIndex(state.lines, state.positionSeconds);
  const visible = selectVisibleLines(state.lines, activeIdx);
  if (visible.length === 0) return;

  const centerX = W / 2;
  const centerY = H / 2;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  for (const rl of visible) {
    const y = centerY + rl.offsetFromActive * (ACTIVE_FONT_PX + LINE_GAP_PX);
    ctx.font = `${rl.offsetFromActive === 0 ? '600' : '500'} ${rl.fontPx}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    ctx.fillStyle = `rgba(255, 255, 255, ${rl.opacity})`;
    // Subtle shadow on active line for legibility
    if (rl.offsetFromActive === 0) {
      ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
      ctx.shadowBlur = 6;
    } else {
      ctx.shadowBlur = 0;
    }
    ctx.fillText(rl.text, centerX, y, W - 40);
  }
  ctx.shadowBlur = 0;
}
